import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';
import {
  ChargingStationLocation,
  ChargingStationLocationDisplay,
  ChargingStationLocationSearchEvent,
} from '../charging-station-location-model';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, debounceTime, distinctUntilChanged, map, skip, startWith, Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { PagedResultV2 } from 'src/app/shared/models/paging';
import { Address } from 'src/app/shared/models/address';
import { Guid } from 'guid-typescript';

interface FilterState {
  query: string | null;
  organizationId: number | null;
  departmentId: number | null;
  locationsWithErrorsInSetup: boolean;
  chargingStationLocationIdentifier: string | null;
  pricePolicyId: number | null;
  priceRegulationIdentifier: string | null;
  priceRegulationName: string | null;
  accessGroupIdentifier: string | null;
}
export enum ChargingStationLocationListQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  departmentId = 'departmentId',
  locationsWithErrorsInSetup = 'locationsWithErrorsInSetup',
  chargingStationLocationIdentifier = 'chargingStationLocationIdentifier',
  pricePolicyId = 'pricePolicyId',
  priceRegulationIdentifier = 'priceRegulationIdentifier',
  priceRegulationName = 'priceRegulationName',
  accessGroupIdentifier = 'accessGroupIdentifier',
}
export class ChargingStationLocationsStateService extends BaseListStateService<
  ChargingStationLocation,
  ChargingStationLocationListQueryParams,
  FilterState
> {
  private locationService = inject(ChargingStationLocationService);
  public length = signal(0);

  protected createQueryParams(filters: Partial<FilterState>): Record<ChargingStationLocationListQueryParams, string | null> {
    const queryParams: Record<ChargingStationLocationListQueryParams, string | null> = {
      [ChargingStationLocationListQueryParams.locationsWithErrorsInSetup]: filters.locationsWithErrorsInSetup
        ? filters.locationsWithErrorsInSetup.toString()
        : null,
      [ChargingStationLocationListQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
      [ChargingStationLocationListQueryParams.departmentId]: filters.departmentId ? filters.departmentId.toString() : null,
      [ChargingStationLocationListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      [ChargingStationLocationListQueryParams.chargingStationLocationIdentifier]: filters.chargingStationLocationIdentifier
        ? filters.chargingStationLocationIdentifier.toString()
        : null,
      [ChargingStationLocationListQueryParams.pricePolicyId]: filters.pricePolicyId ? filters.pricePolicyId.toString() : null,
      [ChargingStationLocationListQueryParams.priceRegulationIdentifier]: filters.priceRegulationIdentifier
        ? filters.priceRegulationIdentifier.toString()
        : null,
      [ChargingStationLocationListQueryParams.priceRegulationName]: filters.priceRegulationName
        ? encodeURIComponent(filters.priceRegulationName)
        : null,
      [ChargingStationLocationListQueryParams.accessGroupIdentifier]: filters.accessGroupIdentifier
        ? filters.accessGroupIdentifier.toString()
        : null,
    };
    return queryParams;
  }

  form: FormGroup<IFilterControls<FilterState>> = new FormGroup<IFilterControls<FilterState>>({
    organizationId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.organizationId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
    departmentId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.departmentId) ?? null;
        return param ? parseInt(param) : null;
      })()
    ),
    query: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.query) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
    accessGroupIdentifier: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.accessGroupIdentifier) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
    chargingStationLocationIdentifier: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.chargingStationLocationIdentifier) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
    locationsWithErrorsInSetup: new FormControl<boolean>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.locationsWithErrorsInSetup) ?? null;
        return param ? decodeURIComponent(param) === 'true' : false;
      })(),
      { nonNullable: true }
    ),
    pricePolicyId: new FormControl<number | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.pricePolicyId) ?? null;
        return param ? parseInt(decodeURIComponent(param)) : null;
      })()
    ),
    priceRegulationIdentifier: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.priceRegulationIdentifier) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
    priceRegulationName: new FormControl<string | null>(
      (() => {
        const param = this.queryParamMap$()?.get(ChargingStationLocationListQueryParams.priceRegulationName) ?? null;
        return param ? decodeURIComponent(param) : null;
      })()
    ),
  });

  locations = signal<PagedResultV2<ChargingStationLocationDisplay>>({
    items: [],
    pageIndex: 0,
    pageSize: this.defaultPageSize,
    hasNextPage: 0,
  });

  $collapseAllLocations = new Subject<void>();

  updateLocationDetails = new Subject<string>();

  focusedChargingStationLocation: WritableSignal<ChargingStationLocationDisplay | null> = signal(null);
  public resetAllFiltersTrigger = signal<{ reset: boolean }>({ reset: false });
  public refreshLocationSubject = new Subject<string>();

  constructor() {
    super();
    this.resetPageOnFilterChange();
    this.length.set(this.viewState().page.pageSize + 1);
    // Watch for query param changes and sync with form (for navigation from quick search while already on the page)
    toObservable(this.queryParamMap$)
      .pipe(
        skip(1), // Skip the initial value since form is already initialized with it
        takeUntilDestroyed()
      )
      .subscribe(queryParamMap => {
        if (!queryParamMap) return;

        const newIdentifier = queryParamMap.get(ChargingStationLocationListQueryParams.chargingStationLocationIdentifier);
        const currentIdentifier = this.form.get('chargingStationLocationIdentifier')?.value;
        const newPriceRegulationIdentifier = queryParamMap.get(ChargingStationLocationListQueryParams.priceRegulationIdentifier);
        const currentPriceRegulationIdentifier = this.form.get('priceRegulationIdentifier')?.value;
        const newPriceRegulationName = queryParamMap.get(ChargingStationLocationListQueryParams.priceRegulationName);
        const currentPriceRegulationName = this.form.get('priceRegulationName')?.value;

        // Only update if deeplinked filter values from URL differ from current form values
        if (
          newIdentifier !== currentIdentifier ||
          newPriceRegulationIdentifier !== currentPriceRegulationIdentifier ||
          newPriceRegulationName !== currentPriceRegulationName
        ) {
          this.form.patchValue(
            {
              chargingStationLocationIdentifier: newIdentifier ? decodeURIComponent(newIdentifier) : null,
              priceRegulationIdentifier: newPriceRegulationIdentifier ? decodeURIComponent(newPriceRegulationIdentifier) : null,
              priceRegulationName: newPriceRegulationName ? decodeURIComponent(newPriceRegulationName) : null,
            },
            { emitEvent: true }
          );
        }
      });

    combineLatest([this.form.valueChanges.pipe(startWith(this.form.value)), toObservable(this.viewState)])
      .pipe(
        distinctUntilChanged(),
        tap(() => this.loading.set(true)),
        debounceTime(100),
        tap(([form, viewState]) => {
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: this.createQueryParams(form),
            queryParamsHandling: 'merge',
          });
        }),
        switchMap(([filters, viewState]) => {
          if (filters.chargingStationLocationIdentifier) {
            const pageEvent = new PageEvent();
            pageEvent.pageSize = 1;
            const placeholderSearchEvent = new ChargingStationLocationSearchEvent(pageEvent);
            placeholderSearchEvent.chargingStationLocationIdentifier = filters.chargingStationLocationIdentifier;
            return this.locationService.getChargingStationLocations(placeholderSearchEvent).pipe(
              map(result => {
                if (result.items.length > 0) {
                  result.items[0].highlight = true;
                }
                const item = result.items.length > 0 ? ({ ...result.items[0] } as ChargingStationLocationDisplay) : null;
                if (item) {
                  this.focusedChargingStationLocation.set(item);
                }
                return result;
              })
            );
          }
          if (!filters.chargingStationLocationIdentifier) {
            this.focusedChargingStationLocation.set(null);
          }

          const searchEvent = new ChargingStationLocationSearchEvent(viewState.page);
          searchEvent.query = filters.query ?? undefined;
          searchEvent.organizationId = filters.organizationId ?? undefined;
          searchEvent.departmentId = filters.departmentId ?? undefined;
          searchEvent.pricePolicyId = filters.pricePolicyId ?? undefined;
          searchEvent.priceRegulationIdentifier = filters.priceRegulationIdentifier ?? undefined;
          searchEvent.accessGroupIdentifier = filters.accessGroupIdentifier ?? undefined;
          searchEvent.locationsWithErrorsInSetup = filters.locationsWithErrorsInSetup;
          searchEvent.sorting = viewState.sort;

          return this.locationService.getChargingStationLocations(searchEvent);
        }),
        tap(res => {
          if (res.hasNextPage) {
            this.length.set(res.pageSize * (res.pageIndex + 1) + 1);
          } else {
            this.length.set(res.pageSize * res.pageIndex + res.items.length);
          }
          res.items.forEach(location => {
            return this.applyBadgeLogic(location);
          });
          this.loading.set(false);
          const items = res.items.map(
            item => ({ ...item, isExpanded: false, randomIdentifier: Guid.create().toString() }) as ChargingStationLocationDisplay
          );
          this.$collapseAllLocations.next();
          this.locations.set({ ...res, items: items });
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.refreshLocationSubject
      .pipe(
        switchMap(locationIdentifier => {
          const searchEvent = new ChargingStationLocationSearchEvent(new PageEvent());
          searchEvent.chargingStationLocationIdentifier = locationIdentifier;
          return this.locationService.getChargingStationLocations(searchEvent);
        }),
        tap(res => {
          if (res.items.length === 1) {
            const updatedLocation = res.items[0];
            this.applyBadgeLogic(updatedLocation);

            const itemIndex = this.locations().items.findIndex(s => s.identifier === updatedLocation.identifier);
            if (itemIndex !== -1) {
              this.locations.update(current => {
                const newItems = [...current.items];
                newItems[itemIndex] = {
                  ...updatedLocation,
                };
                return {
                  ...current,
                  items: newItems,
                };
              });
            }
          }
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public updateSingleChargingStationLocation(chargingStationLocationIdentifier: string | null) {
    if (!chargingStationLocationIdentifier) {
      return;
    }
    this.refreshLocationSubject.next(chargingStationLocationIdentifier);
  }

  public resetAllFilters() {
    this.ToFirstPage({
      organizationId: null,
      departmentId: null,
      query: null,
      locationsWithErrorsInSetup: false,
      chargingStationLocationIdentifier: null,
      pricePolicyId: null,
      priceRegulationIdentifier: null,
      priceRegulationName: null,
      accessGroupIdentifier: null,
    });

    this.focusedChargingStationLocation.set(null);
    this.resetAllFiltersTrigger.set({ reset: true });
  }

  public forceRefresh() {
    // Trigger a refresh by updating the viewState
    this.viewState.update(current => ({ ...current }));
  }

  private applyBadgeLogic(location: ChargingStationLocation): void {
    if (location.anyConnectorWithoutAccessGroup && location.restricted) {
      location.showDangerBadge = true;
      location.dangerBadgeMessage = 'Lokationen er begrænset, men har ladestik uden begrænsning';
    } else if (location.anyConnectorWithoutPricePolicy) {
      location.showDangerBadge = true;
      location.dangerBadgeMessage = 'Denne lokation har ladepunkter uden pris';
    } else if (!location.hasDefaultPricePolicy) {
      location.showDangerBadge = true;
      location.dangerBadgeMessage = 'Denne lokation har ingen standard prispolitik';
    }

    location.address = new Address(
      location.city,
      location.postalCode,
      location.road,
      location.number,
      location.letter,
      location.floor,
      location.door,
      undefined
    );
  }
}
